import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  CheckmarkCircle01Icon,
  Copy01Icon,
  Alert01Icon,
  AttachmentIcon,
  Image01Icon,
  AlertCircleIcon,
  ArrowRight01Icon,
  BankIcon,
  UserIcon,
  InformationCircleIcon,
  Shield02Icon,
  SmartPhone01Icon,
  Money01Icon,
  Refresh01Icon
} from '@hugeicons/core-free-icons';
import Button from '../components/Button';
import ReceiptUpload from '../components/ReceiptUpload';
import ResponsiveModal from '../components/ResponsiveModal';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { FadeLoader } from 'react-spinners';
import ReelText from '../components/ui/ReelText';
import BalanceSyncTutorialModal from '../components/BalanceSyncTutorialModal';

// Maintenance Mode Configuration
const MAINTENANCE_MODE = false;
const MAINTENANCE_END_DATE = new Date('2026-04-09T00:00:00');

const OrderPage = () => {
  const { orderId } = useParams();
  const { user, logout, loading: authLoading } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  
  // Flow State: 'preview' | 'bank_select' | 'payment' | 'deriv_transfer' | 'otp_verify' | 'waiting' | 'success' | 'rejected'
  const [flowStep, setFlowStep] = useState('preview');
  const [transactionUuid, setTransactionUuid] = useState(null);
  const [transaction, setTransaction] = useState(null);
  const [rejectionReason, setRejectionReason] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorState, setErrorState] = useState(null); // null | 'expired' | 'not_found'
  const [validatingSession, setValidatingSession] = useState(true);
  const [showAgentWarning, setShowAgentWarning] = useState(false);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null); 
  const [processing, setProcessing] = useState(false);
  const [showConfirmRelease, setShowConfirmRelease] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isContactAgentModalOpen, setIsContactAgentModalOpen] = useState(false);
  const [agentContactDetails, setAgentContactDetails] = useState(null);
  const [loadingAgentContact, setLoadingAgentContact] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);
  // Balance Sync Tutorial Modal
  const [showBalanceSyncTutorial, setShowBalanceSyncTutorial] = useState(false);
  const [hasCheckedTutorial, setHasCheckedTutorial] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  
  // Payment Input State
  const [payAmountUsd, setPayAmountUsd] = useState(0);
  const [payAmountNgn, setPayAmountNgn] = useState('');
  const [amountError, setAmountError] = useState('');

  // Bank State (for Withdrawals)
  const [clientBanks, setClientBanks] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [clientBalance, setClientBalance] = useState(null);

  // Timer State (20 mins)
  const [timeLeft, setTimeLeft] = useState(1200);
  const timerRef = useRef(null);

  // Waiting Texts Animation
  const waitingTexts = [
    "Verifying your transfer...",
    "Waiting for agent confirmation...",
    "Almost there, don't close this page...",
    "Agent is checking their bank app...",
    "Finalizing release..."
  ];
  
  const withdrawalWaitingTexts = [
    "Verifying your USD transfer...",
    "Agent is confirming receipt...",
    "Processing Naira release...",
    "Almost there, check your bank app soon...",
    "Finalizing transaction..."
  ];

  const [activeTextIndex, setActiveTextIndex] = useState(0);

  // Show tutorial modal when order completes
  useEffect(() => {
    if (flowStep === 'success' && !hasCheckedTutorial) {
      setHasCheckedTutorial(true);
      api.get('/balance-sync-tutorial/status')
        .then(res => {
          if (res.data.data.should_show) {
            setShowBalanceSyncTutorial(true);
          }
        })
        .catch(() => {});
    }
  }, [flowStep, hasCheckedTutorial]);

  useEffect(() => {
    if (!authLoading) {
      if (user?.role === 'agent') setShowAgentWarning(true);
      setValidatingSession(false);
    }
  }, [user, authLoading]);

  const fetchOrder = async () => {
    try {
      const response = await api.get(`/orders/${orderId}`);
      const orderData = response.data.order;
      setOrder(orderData);

      // Sync flow step based on server state
      if (orderData.status === 'pending_verification') setFlowStep('waiting');
      if (orderData.status === 'completed') setFlowStep('success');

      // If order is active but user has an existing transaction, jump to the correct step
      if (orderData.transactions && orderData.transactions.length > 0) {
        const myTx = orderData.transactions[0]; // Backend only returns current client's tx
        setTransaction(myTx);
        setTransactionUuid(myTx.uid);
        if (myTx.status === 'completed') setFlowStep('success');
        else if (['pending', 'pending_verification', 'processing', 'verified'].includes(myTx.status)) setFlowStep('waiting');
      }

    } catch (err) {
      if (err.status === 410) {
        // We need to know if this specific client already had a transaction
        const orderData = err.data?.order || err.data;
        if (orderData) setOrder(orderData); // CRITICAL: Update order state even on error to get spread/rate

        if (orderData?.transactions && orderData.transactions.length > 0) {
          const myTx = orderData.transactions[0];
          setTransaction(myTx);
          setTransactionUuid(myTx.uid);
          if (myTx.status === 'completed') setFlowStep('success');
          else if (['pending', 'pending_verification', 'processing', 'verified'].includes(myTx.status)) setFlowStep('waiting');
          else if (myTx.status === 'rejected') setFlowStep('rejected');
          return; // Skip setting errorState
        }

        setErrorState('expired');
        if (err.data?.merchant) {
          setOrder({ merchant: err.data.merchant });
        }
      } else if (err.status === 404) {
        setErrorState('not_found');
      } else {
        console.error('Failed to fetch order:', err);
        setErrorState('expired');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchClientBanks = async () => {
    try {
      const response = await api.get('/bank-accounts');
      const accounts = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setClientBanks(accounts);
    } catch (err) {
      console.warn('Failed to fetch banks:', err);
    }
  };

  const fetchClientBalance = async () => {
    try {
      const response = await api.get('/deriv/linked-accounts');
      const account = response.data?.data?.account || response.data?.account;
      if (account) setClientBalance(parseFloat(account.current_balance));
    } catch (err) {
      console.warn('Failed to fetch client balance:', err);
    }
  };

  useEffect(() => {
    if (flowStep === 'waiting' && transactionUuid) {
      const checkStatus = async () => {
        try {
          const res = await api.get(`/client/transactions/${transactionUuid}`);
          const tx = res.data.transaction;
          
          // Update transaction state so UI can react to status changes (like 'verified')
          setTransaction(tx);
          
          if (tx.status === 'completed') setFlowStep('success');
          if (tx.status === 'verified') {
            // For withdrawals, verified means USD is received by agent, but Naira is pending.
            // Stay in waiting room but maybe update UI message (handled by waitingTexts logic)
            console.log("USD Secured. Waiting for agent to release Naira...");
          }
          if (tx.status === 'processing') {
            // Option: Update waiting text or show subtle progress
            console.log("Agent is currently releasing funds...");
          }
          if (tx.status === 'rejected') {
            setFlowStep('rejected');
            setRejectionReason(tx.agent_note);
          }
        } catch (err) {
          console.error('Status check failed:', err);
        }
      };

      const statusInterval = setInterval(checkStatus, 1500);
      return () => clearInterval(statusInterval);
    }
  }, [flowStep, transactionUuid]);

  useEffect(() => {
    fetchOrder();
    const interval = setInterval(fetchOrder, 1500);
    return () => clearInterval(interval);
  }, [orderId]);

  useEffect(() => {
    if (order?.transaction_type === 'withdrawal') {
        fetchClientBanks();
        fetchClientBalance();
    }
  }, [order]);

  useEffect(() => {
    if (flowStep === 'payment' || flowStep === 'waiting') {
      timerRef.current = setInterval(() => setTimeLeft(prev => (prev > 0 ? prev - 1 : 0)), 1000);
      const textInterval = setInterval(() => setActiveTextIndex(prev => (prev + 1) % waitingTexts.length), 4000);
      return () => { clearInterval(timerRef.current); clearInterval(textInterval); };
    }
  }, [flowStep]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAmountChange = (e) => {
    // Only allow numbers and one decimal point
    let val = e.target.value.replace(/[^0-9.]/g, '');
    
    // Prevent multiple decimal points
    const parts = val.split('.');
    if (parts.length > 2) val = parts[0] + '.' + parts.slice(1).join('');
    
    // Auto-truncate to 2 decimal places
    if (parts.length === 2 && parts[1].length > 2) {
      val = parts[0] + '.' + parts[1].slice(0, 2);
    }
    
    setPayAmountNgn(val);
    
    if (!val || isNaN(val)) { 
      setPayAmountUsd(0); 
      setAmountError(''); 
      return; 
    }

    const ngn = parseFloat(val);
    const rate = parseFloat(order.current_rate);
    const usd = ngn / rate;
    setPayAmountUsd(usd);

    const min = parseFloat(order.min_amount_usd || 0);
    const remaining = parseFloat(order.remaining_amount_usd || 0);

    if (usd < min) setAmountError(`Min: $${min.toFixed(2)} / ₦${(min * rate).toLocaleString()}`);
    else if (usd > remaining) setAmountError(`Max: $${remaining.toFixed(2)} / ₦${(remaining * rate).toLocaleString()}`);
    else setAmountError('');
  };

  const copyToClipboard = async (text) => {
    if (!text) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        addToast('Copied to clipboard!', 'success');
      } else {
        // Fallback for non-secure contexts
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
          addToast('Copied to clipboard!', 'success');
        } catch (err) {
          console.error('Fallback copy failed', err);
          addToast('Failed to copy. Please copy manually.', 'error');
        }
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Copy failed', err);
      addToast('Failed to copy. Please copy manually.', 'error');
    }
  };

  const handleInitiateOrder = () => {
    // Check maintenance mode
    if (MAINTENANCE_MODE && new Date() < MAINTENANCE_END_DATE) {
      setShowMaintenanceModal(true);
      return;
    }
    
    // For deposits: skip bank selection (use agent's bank from order)
    // For withdrawals: show bank selection (client selects their bank)
    if (order.transaction_type === 'withdrawal') setFlowStep('bank_select');
    else setFlowStep('payment');
  };

  const handleBankConfirm = () => {
    if (!selectedBankId) { addToast("Please select a receiving bank.", "error"); return; }
    setFlowStep('payment');
  };

  const handleProceedToTransfer = async () => {
    if (amountError || !payAmountUsd) { addToast("Invalid amount.", "error"); return; }
    
    // Withdrawal PoL Check
    if (isWithdrawal && clientBalance !== null && parseFloat(payAmountUsd) > clientBalance) {
        addToast(`Insufficient USD balance. Your current balance is $${clientBalance.toFixed(2)}`, "error");
        return;
    }

    if (isWithdrawal) {
      setProcessing(true);
      try {
        await api.post(`/client/orders/${order.link_token}/request-withdrawal-otp`);
        addToast("Verification code sent to your email.", "success");
        setFlowStep('otp_verify');
      } catch (err) {
        addToast(err.response?.data?.message || "Failed to trigger verification.", "error");
      } finally {
        setProcessing(false);
      }
    } else {
      setFlowStep('deriv_transfer');
    }
  };

  const handlePaid = async () => {
    const isWithdrawal = order.transaction_type === 'withdrawal';

    // Safety check for withdrawal
    if (isWithdrawal && !showConfirmRelease) {
      setShowConfirmRelease(true);
      return;
    }

    if (!isWithdrawal && !selectedReceipt) { addToast("Please upload proof of payment.", "error"); return; }
    if (amountError || !payAmountUsd) { addToast("Please enter a valid amount within limits.", "error"); return; }

    setProcessing(true);
    try {
      const payload = {
        amount_sent: payAmountNgn,
        client_note: isWithdrawal ? `Automated USD Withdrawal` : `Client paid NGN`,
        // For deposits: use order's bank_account_id (agent's bank)
        // For withdrawals: use selectedBankId (client's bank)
        bank_account_id: isWithdrawal ? selectedBankId : order.bank_account_id,
        verification_code: isWithdrawal ? verificationCode : null
      };

      const response = await api.post(`/client/orders/${order.link_token}/pay`, payload);
      
      if (response.data.transaction?.uid) {
        setTransactionUuid(response.data.transaction.uid);
      }

      if (!isWithdrawal && selectedReceipt && response.data.transaction?.reference) {
        const formData = new FormData();
        formData.append('receipt', selectedReceipt);
        formData.append('transaction_reference', response.data.transaction.reference);
        await api.postFile(`/client/orders/${order.link_token}/receipt`, formData);
      }

      addToast(isWithdrawal ? "USD Release logged!" : "Payment submitted for verification.", "success");
      setShowConfirmRelease(false);
      setFlowStep('waiting');
    } catch (err) {
      addToast(err.message || "Something went wrong", "error");
    } finally {
      setProcessing(false);
    }
  };

  const handleLogoutAndSwitch = () => { logout(); navigate('/login'); };

  const handleOpenContactAgent = async () => {
    if (!order?.merchant?.uid) {
      addToast('Agent contact info unavailable', 'error');
      return;
    }
    setIsContactAgentModalOpen(true);
    setLoadingAgentContact(true);
    try {
      const response = await api.get(`/client/agents/${order.merchant.uid}/contact`);
      setAgentContactDetails(response.data.agent);
    } catch (err) {
      console.error('Failed to fetch agent contact:', err);
      addToast('Failed to load agent contact details', 'error');
      setIsContactAgentModalOpen(false);
    } finally {
      setLoadingAgentContact(false);
    }
  };

  const handleCopyPhone = () => {
    if (!agentContactDetails?.phone_number) return;
    navigator.clipboard.writeText(agentContactDetails.phone_number);
    setCopiedPhone(true);
    addToast('Phone number copied!', 'success');
    setTimeout(() => setCopiedPhone(false), 2000);
  };

  const handleCallAgent = () => {
    if (!agentContactDetails?.phone_number) return;
    window.location.href = `tel:${agentContactDetails.phone_number}`;
  };

  if (validatingSession || authLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-off-white dark:bg-dark-bg">
        <FadeLoader color="#4ade80" />
        <p className="mt-4 text-muted-gray font-black uppercase tracking-widest text-[10px]">Verifying Session...</p>
      </div>
    );
  }

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><FadeLoader color="#4ade80" /></div>;

  // If we have a finalized or waiting transaction, show that UI instead of the generic expired screen
  if (errorState === 'expired' && (flowStep === 'success' || flowStep === 'waiting' || flowStep === 'rejected')) {
    // Let the flowStep handle rendering below
  } else if (errorState === 'expired' || errorState === 'not_found') {
    return (
      <>
        <div className="max-w-lg mx-auto pb-20 px-4">
          <div className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-xl p-10 rounded-[3rem] border border-red-200 dark:border-red-800/30 shadow-float-lg text-center">
            <div className="bg-red-500/10 p-8 rounded-full inline-block mb-6 text-red-500 animate-pulse">
              <HugeiconsIcon icon={AlertCircleIcon} size={64} />
            </div>
            <h1 className="text-3xl font-black text-charcoal dark:text-white mb-4">
              {errorState === 'not_found' ? 'Order Not Found' : 'Link Expired'}
            </h1>
            <p className="text-muted-gray text-lg font-medium mb-2">
              {errorState === 'not_found'
                ? 'This order does not exist or has been removed.'
                : 'This payment link is no longer active.'}
            </p>
            <p className="text-muted-gray text-sm mb-8">
              Please contact your agent to get a new link.
            </p>
            <div className="bg-off-white/50 dark:bg-gray-800/50 p-6 rounded-3xl border border-light-gray/20 dark:border-gray-700/20 mb-6">
              <p className="text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2">Why did this happen?</p>
              <p className="text-sm text-muted-gray font-medium leading-relaxed">
                {errorState === 'not_found'
                  ? 'This order may have been deleted, cancelled, or never existed. Double-check the link you received.'
                  : 'Payment links expire after their capacity is filled or after a period of inactivity. Your agent will need to generate a fresh order link for you to proceed with the transaction.'}
              </p>
            </div>
            {order?.merchant?.name && (
              <div className="bg-off-white/50 dark:bg-gray-800/50 p-6 rounded-3xl border border-light-gray/20 dark:border-gray-700/20 mb-6">
                <p className="text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2">Your Agent</p>
                <p className="text-xl font-black text-charcoal dark:text-white">{order.merchant.name}</p>
              </div>
            )}
            <div className="space-y-3">
              <Button onClick={() => navigate('/')} variant="secondary" className="w-full py-4 rounded-2xl font-black">
                Back to Safety
              </Button>
              {order?.merchant?.uid && (
                <Button onClick={handleOpenContactAgent} className="w-full py-4 rounded-2xl font-black gap-2">
                  <HugeiconsIcon icon={SmartPhone01Icon} size={20} />
                  Contact Agent
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Contact Agent Modal for Expired Page */}
        <ResponsiveModal isOpen={isContactAgentModalOpen} onClose={() => setIsContactAgentModalOpen(false)} title="Contact Agent">
          <div className="py-4 space-y-4">
            {loadingAgentContact ? (
              <div className="py-12 flex items-center justify-center">
                <FadeLoader color="#4ade80" />
              </div>
            ) : agentContactDetails ? (
              <>
                {/* Agent Info */}
                <div className="flex items-center gap-4 p-4 bg-off-white/50 dark:bg-gray-800/50 rounded-3xl border border-light-gray/20 dark:border-gray-700/20">
                  <div className="w-14 h-14 rounded-2xl bg-soft-green/10 text-soft-green flex items-center justify-center text-xl font-black uppercase">
                    {(agentContactDetails.name || 'A')[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-black text-charcoal dark:text-white truncate">{agentContactDetails.name}</h3>
                    {agentContactDetails.is_verified && (
                      <div className="flex items-center gap-1 mt-1">
                        <HugeiconsIcon icon={Shield02Icon} size={14} className="text-soft-green" />
                        <span className="text-[10px] font-black uppercase text-soft-green tracking-widest">Verified</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Phone */}
                {agentContactDetails.phone_number && (
                  <div className="bg-off-white/50 dark:bg-gray-800/50 p-5 rounded-3xl border border-light-gray/20 dark:border-gray-700/20">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2.5 rounded-xl bg-soft-green/10 text-soft-green">
                        <HugeiconsIcon icon={SmartPhone01Icon} size={20} />
                      </div>
                      <div>
                        <p className="text-[9px] font-black text-muted-gray uppercase tracking-widest">Phone Number</p>
                        <p className="text-base font-black text-charcoal dark:text-white">{agentContactDetails.phone_number}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleCallAgent} className="flex-1 py-3 rounded-xl font-black gap-2 text-sm">
                        <HugeiconsIcon icon={SmartPhone01Icon} size={16} />
                        Call
                      </Button>
                      <button
                        onClick={handleCopyPhone}
                        className={`p-3 rounded-xl border-2 transition-all ${copiedPhone ? 'bg-soft-green text-white border-soft-green' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-light-gray/20 dark:border-gray-700 hover:border-soft-green'}`}
                      >
                        <HugeiconsIcon icon={copiedPhone ? CheckmarkCircle01Icon : Copy01Icon} size={18} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Info Box */}
                <div className="p-4 bg-soft-green/5 dark:bg-soft-green/10 rounded-2xl border border-soft-green/20">
                  <div className="flex items-start gap-2">
                    <HugeiconsIcon icon={UserIcon} size={18} className="text-soft-green shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[9px] font-black text-soft-green uppercase tracking-widest mb-0.5">Need a New Link?</p>
                      <p className="text-xs text-muted-gray font-medium leading-relaxed">
                        Contact your agent to receive a fresh payment link.
                      </p>
                    </div>
                  </div>
                </div>

                <Button onClick={() => setIsContactAgentModalOpen(false)} className="w-full py-3.5 rounded-2xl font-black">
                  Done
                </Button>
              </>
            ) : (
              <div className="py-12 text-center">
                <HugeiconsIcon icon={AlertCircleIcon} size={48} className="text-red-500 mx-auto mb-4" />
                <p className="text-muted-gray font-bold">Failed to load agent details</p>
                <Button onClick={() => setIsContactAgentModalOpen(false)} variant="secondary" className="mt-4">Close</Button>
              </div>
            )}
          </div>
        </ResponsiveModal>
      </>
    );
  }

  if (!order) return <div className="text-center py-20 text-muted-gray font-black uppercase">Order Unavailable</div>;

  const effectiveRate = parseFloat(order.current_rate);
  const isWithdrawal = order.transaction_type === 'withdrawal';

  return (
    <div className="max-w-2xl mx-auto pb-20 px-4">
      <ReceiptUpload 
        isOpen={isReceiptModalOpen} 
        onClose={() => setIsReceiptModalOpen(false)} 
        onUpload={(file) => { setSelectedReceipt(file); setIsReceiptModalOpen(false); }} 
      />

      <ResponsiveModal isOpen={showAgentWarning} onClose={() => {}} title="Restricted">
        <div className="py-6 text-center">
          <HugeiconsIcon icon={AlertCircleIcon} size={48} className="text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-black mb-2">Unauthorized</h3>
          <p className="text-muted-gray mb-8 px-4 text-sm">Agents cannot use payment links. Please switch to a <b>Client</b> account.</p>
          <Button onClick={handleLogoutAndSwitch} className="w-full py-4 rounded-2xl bg-red-500 text-white font-black">Switch Account</Button>
        </div>
      </ResponsiveModal>

      {/* Maintenance Mode Modal */}
      <ResponsiveModal isOpen={showMaintenanceModal} onClose={() => setShowMaintenanceModal(false)} title="System Maintenance">
        <div className="py-6 text-center space-y-6">
          <div className="bg-yellow-500/10 dark:bg-yellow-500/20 p-8 rounded-full inline-block">
            <HugeiconsIcon icon={Refresh01Icon} size={64} className="text-yellow-500 animate-spin" />
          </div>

          <div>
            <h3 className="text-2xl font-black text-charcoal dark:text-white mb-3">Maintenance in Progress</h3>
            <p className="text-muted-gray px-4 text-sm font-medium leading-relaxed">
              We're currently performing system upgrades. Order creation is temporarily disabled.
            </p>
          </div>

          <div className="bg-off-white/50 dark:bg-gray-800/50 p-6 rounded-3xl border border-light-gray/20 dark:border-gray-700/20 mx-4">
            <p className="text-[10px] font-black text-muted-gray uppercase tracking-widest mb-2">Expected Completion</p>
            <p className="text-xl font-black text-charcoal dark:text-white">
              {MAINTENANCE_END_DATE.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
            <p className="text-xs text-muted-gray mt-2">All order operations will resume automatically after this date.</p>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/10 p-4 rounded-2xl border border-blue-100 dark:border-blue-800/30 mx-4">
            <p className="text-xs text-blue-700 dark:text-blue-300 font-medium">
              Need urgent assistance? Please contact your agent directly or reach out to support.
            </p>
          </div>

          <Button onClick={() => setShowMaintenanceModal(false)} variant="secondary" className="w-full py-4 rounded-2xl font-black">
            Got It
          </Button>
        </div>
      </ResponsiveModal>

      {/* Withdrawal Confirmation Modal */}
      <ResponsiveModal isOpen={showConfirmRelease} onClose={() => setShowConfirmRelease(false)} title="Confirm Secure Withdrawal">
        <div className="py-6 text-center space-y-6">
          <div className="bg-soft-green/5 dark:bg-soft-green/10 p-6 rounded-full inline-block text-soft-green">
            <HugeiconsIcon icon={Shield02Icon} size={48} />
          </div>

          <div>
            <h3 className="text-xl font-black text-charcoal dark:text-white mb-2">Initialize Secure Transfer?</h3>
            <p className="text-muted-gray px-4 text-sm font-medium leading-relaxed">
              By clicking confirm, you authorize Safzi to securely transfer <b>${parseFloat(payAmountUsd || 0).toFixed(2)}</b> from your Deriv account to the agent.
            </p>
          </div>

          <div className="bg-soft-green/5 dark:bg-soft-green/10 p-4 rounded-2xl border border-soft-green/10 mx-4">
            <p className="text-[10px] font-black text-soft-green uppercase tracking-widest leading-tight">
              Security Note: This transfer is automated and irreversible once authorized with your OTP.
            </p>
          </div>

          <div className="flex gap-3 px-4 pt-2">
            <Button variant="secondary" onClick={() => setShowConfirmRelease(false)} className="flex-1 py-4 rounded-2xl font-bold">Cancel</Button>
            <Button onClick={handlePaid} disabled={processing} className="flex-[2] py-4 rounded-2xl font-black shadow-lg">
              {processing ? 'Processing...' : 'Yes, Authorize Transfer'}
            </Button>
          </div>
        </div>
      </ResponsiveModal>

      {/* --- PREVIEW STEP --- */}
      {flowStep === 'preview' && (
        <div className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-xl p-8 rounded-[3rem] border border-light-gray/20 dark:border-dark-border/20 shadow-float-lg text-center">
          <div className="bg-soft-green/10 p-6 rounded-full inline-block mb-6 text-soft-green">
            <HugeiconsIcon icon={UserIcon} size={48} />
          </div>
          
          <div className="flex items-center justify-center gap-2 mb-2">
            <h1 className="text-3xl font-black text-charcoal dark:text-white tracking-tight"><ReelText text={order.merchant?.name || 'Authorized Agent'} /></h1>
            {order.agent_liquidity === 0 && !isWithdrawal && (
              <div className="text-yellow-500" title="Low Liquidity">
                <HugeiconsIcon icon={AlertCircleIcon} size={24} />
              </div>
            )}
          </div>

          <div className={`inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase mb-8 ${isWithdrawal ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
            {isWithdrawal ? 'Withdrawal' : 'Deposit'} Order
          </div>

          {/* Liquidity Warning */}
          {order.agent_liquidity === 0 && !isWithdrawal && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-2xl border border-yellow-100 dark:border-yellow-800/30 mb-8 mx-auto max-w-sm">
              <p className="text-xs font-bold text-yellow-700 dark:text-yellow-300">
                Warning: This agent has $0.00 USD in their Deriv account. Be sure before you proceed.
              </p>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-off-white/50 dark:bg-gray-800/50 p-4 rounded-3xl border border-light-gray/10 text-left min-w-0">
              <p className="text-[10px] font-black text-muted-gray uppercase tracking-widest mb-1">Exchange Rate</p>
              <p className="font-black text-charcoal dark:text-white text-lg truncate"><ReelText text={`₦${effectiveRate.toLocaleString()}/$`} /></p>
            </div>
            <div className="bg-off-white/50 dark:bg-gray-800/50 p-4 rounded-3xl border border-light-gray/10 text-left min-w-0">
              <p className="text-[10px] font-black text-muted-gray uppercase tracking-widest mb-1">Order Limits</p>
              <p className="font-black text-charcoal dark:text-white text-lg truncate" title={`$${order.min_amount_usd} - $${order.total_amount_usd}`}>${order.min_amount_usd} - ${order.total_amount_usd}</p>
            </div>
          </div>

          <Button onClick={handleInitiateOrder} className="w-full py-5 rounded-[2rem] text-xl font-black shadow-float-lg gap-2">
            {isWithdrawal ? 'Start Withdrawal' : 'Buy USD Now'} <HugeiconsIcon icon={ArrowRight01Icon} size={24} />
          </Button>
        </div>
      )}

      {/* --- CAROUSEL FLOW --- */}
      {flowStep !== 'preview' && (
        <div className="space-y-8">
          {/* Progress Indicator */}
          <div className="flex justify-between items-center px-4 relative max-w-sm mx-auto">
            <div className="absolute left-0 top-1/2 w-full h-0.5 bg-gray-200 dark:bg-gray-800 -z-10"></div>
            {[1, 2, 3].map(s => {
              const active = (s === 1 && (flowStep === 'bank_select' || flowStep === 'payment' || flowStep === 'deriv_transfer' || flowStep === 'otp_verify')) || (s === 2 && flowStep === 'waiting') || (s === 3 && (flowStep === 'success' || flowStep === 'rejected'));
              const done = (s === 1 && (flowStep === 'waiting' || flowStep === 'success' || flowStep === 'rejected')) || (s === 2 && (flowStep === 'success' || flowStep === 'rejected'));
              return (
                <div key={s} className={`w-10 h-10 rounded-full flex items-center justify-center font-black transition-all duration-500 border-4 ${
                  active ? 'bg-soft-green text-white border-off-white dark:border-dark-bg scale-125 shadow-lg' : 
                  done ? 'bg-soft-green/20 text-soft-green border-soft-green' : 'bg-white dark:bg-gray-800 text-muted-gray border-gray-100 dark:border-gray-700'
                }`}>
                  {done ? <HugeiconsIcon icon={CheckmarkCircle01Icon} size={20} /> : s}
                </div>
              );
            })}
          </div>

          <div className="overflow-hidden">
            
            {/* 0. Bank Selection (Withdrawal Only) */}
            {flowStep === 'bank_select' && (
              <div className="p-4 sm:p-8 space-y-6">
                <div className="text-center">
                  <h2 className="text-2xl font-black text-charcoal dark:text-white">Receive Naira To</h2>
                  <p className="text-muted-gray text-sm mt-1">Select the bank where the agent will pay you</p>
                </div>
                {clientBanks.length === 0 ? (
                  <div className="p-8 bg-off-white dark:bg-gray-800/50 rounded-3xl text-center border-2 border-dashed border-gray-200">
                    <p className="text-sm font-bold text-muted-gray mb-4">You haven't added any banks.</p>
                    <Button onClick={() => navigate('/profile')} variant="secondary" className="px-6 py-2 text-xs">Go to Profile</Button>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                    {clientBanks.map(bank => (
                      <div key={bank.id} onClick={() => setSelectedBankId(bank.id)} className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${selectedBankId === bank.id ? 'border-soft-green bg-soft-green/5' : 'border-light-gray/10 hover:border-soft-green/30'}`}>
                        <p className="font-bold text-sm text-charcoal dark:text-white">{bank.bank_name}</p>
                        <p className="text-[10px] text-muted-gray font-black uppercase">{bank.account_number} • {bank.account_name}</p>
                      </div>
                    ))}
                  </div>
                )}
                <Button onClick={handleBankConfirm} disabled={!selectedBankId} className="w-full py-4 rounded-2xl font-black">Confirm Bank <HugeiconsIcon icon={ArrowRight01Icon} size={20} className="ml-2" /></Button>
              </div>
            )}

            {/* 1. Payment/Transfer Step */}
            {flowStep === 'payment' && (
              <div className="p-4 sm:p-8 space-y-8">
                <div className="text-center">
                  <h2 className="text-2xl font-black text-charcoal dark:text-white">{isWithdrawal ? 'Transfer to Agent' : 'Complete Transfer'}</h2>
                  <p className="text-muted-gray text-sm mt-1">{isWithdrawal ? `Send USD to Agent's Deriv CR account` : `Pay NGN to Agent's bank account`}</p>
                </div>

                <div className="bg-soft-green/5 dark:bg-soft-green/10 p-6 rounded-[2.5rem] border border-soft-green/20">
                  <label className="block text-xs font-black text-soft-green uppercase tracking-widest mb-3 px-1">
                    {isWithdrawal ? 'Amount to Withdraw (USD)' : 'I WANT TO DEPOSIT (NGN)'}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={isWithdrawal ? payAmountUsd || '' : payAmountNgn}
                      onChange={isWithdrawal ? (e) => {
                        const val = e.target.value.replace(/[^0-9.]/g, '');
                        setPayAmountUsd(val);
                        setPayAmountNgn(val ? (parseFloat(val) * effectiveRate).toString() : '');
                      } : handleAmountChange}
                      placeholder={isWithdrawal ? "0.00" : "Enter amount in NAIRA"}
                      className="w-full bg-white dark:bg-dark-surface text-3xl font-black outline-none text-charcoal dark:text-white placeholder:text-sm placeholder:font-bold placeholder:text-muted-gray/40 border-4 border-soft-green rounded-2xl px-4 py-3 focus:ring-4 focus:ring-soft-green/30 transition-all shadow-lg"
                    />
                  </div>
                  <p className="text-[10px] font-black text-soft-green/60 uppercase tracking-widest mt-2 px-1">
                    {isWithdrawal ? `Min: $${order.min_amount_usd || '5'}` : `Min: $${order.min_amount_usd || '5'} / ₦${((order.min_amount_usd || 5) * effectiveRate).toLocaleString()}`}
                  </p>
                  {Boolean((isWithdrawal && payAmountUsd > 0) || (!isWithdrawal && payAmountNgn > 0)) && (
                    <div className="mt-4 pt-4 border-t border-soft-green/10 flex justify-between items-center text-muted-gray font-bold text-xs uppercase tracking-widest">
                      <span>{isWithdrawal ? 'You Receive (NGN)' : 'Total USD Receive'}</span>
                      <span className="text-soft-green font-black text-lg">
                        {isWithdrawal ? `₦${parseFloat(payAmountNgn).toLocaleString()}` : `$${parseFloat(payAmountUsd).toFixed(2)}`}
                      </span>
                    </div>
                  )}
                  {amountError && <p className="text-xs font-bold text-red-500 mt-2">{amountError}</p>}

                  {Boolean(!isWithdrawal && payAmountUsd && payAmountUsd > 0) && (                    <div className={`mt-4 p-3 rounded-xl border flex items-center gap-2 ${
                      parseFloat(payAmountUsd) > parseFloat(order.agent_liquidity || 0) 
                        ? 'bg-red-50 border-red-100 text-red-600 dark:bg-red-900/20 dark:border-red-800/30 dark:text-red-400' 
                        : 'bg-green-50 border-green-100 text-green-600 dark:bg-green-900/20 dark:border-green-800/30 dark:text-green-400'
                    }`}>
                      <HugeiconsIcon icon={parseFloat(payAmountUsd) > parseFloat(order.agent_liquidity || 0) ? Alert01Icon : CheckmarkCircle01Icon} size={16} />
                      <p className="text-[10px] font-black uppercase tracking-widest">
                        {parseFloat(payAmountUsd) > parseFloat(order.agent_liquidity || 0) 
                          ? `Insufficient Agent Liquidity (Max: $${parseFloat(order.agent_liquidity).toFixed(2)})` 
                          : 'Proof of Liquidity: Verified'}
                      </p>
                    </div>
                  )}

                  {Boolean(!isWithdrawal && payAmountUsd > 0 && order.cashback_rate > 0) && (
                    <div className="mt-3 p-4 bg-purple-500/10 border border-purple-500/20 rounded-2xl animate-in zoom-in-95 duration-300">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <HugeiconsIcon icon={Money01Icon} size={16} className="text-purple-500" />
                          <span className="text-[10px] font-black text-purple-500 uppercase tracking-widest">Cashback Reward</span>
                        </div>
                        <span className="text-sm font-black text-purple-500">₦{(parseFloat(payAmountUsd) * parseFloat(order.spread || 0) * parseFloat(order.cashback_rate)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <p className="text-[9px] font-bold text-muted-gray mt-1">This amount will be added to your rewards wallet upon completion.</p>
                    </div>
                  )}
                </div>

                {!isWithdrawal ? (
                  <div className="space-y-4">
                    {Object.entries(order.bank_details || {}).map(([key, value]) => (
                      <div key={key} className="flex justify-between items-center py-4 border-b border-gray-50 dark:border-gray-800 last:border-0">
                        <span className="text-muted-gray text-[10px] font-black uppercase tracking-widest">{key.replace(/_/g, ' ')}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-charcoal dark:text-white select-all">{value}</span>
                          <button onClick={() => copyToClipboard(value)} className="text-soft-green"><HugeiconsIcon icon={Copy01Icon} size={16} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-purple-50 dark:bg-purple-900/10 p-6 rounded-3xl border border-purple-100 dark:border-purple-800/30">
                    <div className="flex items-start gap-3">
                      <HugeiconsIcon icon={InformationCircleIcon} className="text-purple-500 shrink-0" size={20} />
                      <div>
                        <p className="text-xs text-purple-800 dark:text-purple-200 font-bold uppercase tracking-wide">Withdrawal Instruction</p>
                        <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">You will be taken to Deriv to securely release <b>${payAmountUsd || '0.00'} USD</b> to <b>{order.merchant?.name}</b>.</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-4">
                  {!isWithdrawal && (
                    <button onClick={() => setIsReceiptModalOpen(true)} className={`w-full py-4 rounded-2xl border-2 border-dashed flex items-center justify-center gap-2 transition-all ${selectedReceipt ? 'border-soft-green bg-soft-green/5 text-soft-green' : 'border-gray-200 dark:border-gray-700 text-muted-gray'}`}>
                      <HugeiconsIcon icon={selectedReceipt ? Image01Icon : AttachmentIcon} size={20} />
                      <span className="font-bold text-sm">{selectedReceipt ? selectedReceipt.name : 'Upload Payment Proof'}</span>
                    </button>
                  )}
                  
                  {isWithdrawal ? (
                    <Button onClick={handleProceedToTransfer} className="w-full py-5 rounded-[2.5rem] font-black text-lg shadow-lg">
                      Proceed to Transfer <HugeiconsIcon icon={ArrowRight01Icon} size={20} className="ml-2" />
                    </Button>
                  ) : (
                    <Button onClick={handlePaid} disabled={processing || !selectedReceipt || !!amountError || !payAmountUsd} className="w-full py-5 rounded-[2.5rem] font-black text-lg shadow-lg">
                      I Have Transferred Naira
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* 1.5 Automated OTP Verification (Withdrawal Only) */}
            {flowStep === 'otp_verify' && (
              <div className="p-4 sm:p-8 space-y-8 animate-in slide-in-from-right-8 duration-300">
                <div className="text-center">
                  <div className="bg-soft-green/10 p-6 rounded-full inline-block mb-4 text-soft-green">
                    <HugeiconsIcon icon={Shield02Icon} size={48} />
                  </div>
                  <h2 className="text-2xl font-black text-charcoal dark:text-white">Verify Transfer</h2>
                  <p className="text-muted-gray text-sm mt-1">Enter the verification code sent to your Deriv email.</p>
                </div>

                <div className="space-y-6">
                  <div className="bg-soft-green/5 dark:bg-soft-green/10 p-8 rounded-[2.5rem] border border-soft-green/20 text-center">
                    <label className="block text-[10px] font-black text-soft-green uppercase tracking-[0.2em] mb-4 px-1">Deriv Verification Code</label>
                    <input 
                      type="text"
                      maxLength="10"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      placeholder="TOKEN"
                      className="w-full bg-transparent text-5xl font-black text-center outline-none text-charcoal dark:text-white placeholder:text-muted-gray/20 tracking-widest"
                    />
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-900/10 p-6 rounded-[2rem] border border-blue-100 dark:border-blue-800/30">
                    <p className="text-xs text-blue-700 dark:text-blue-300 font-medium text-center leading-relaxed">
                      This code authorizes Safzi to securely transfer <b>${parseFloat(payAmountUsd).toFixed(2)}</b> from your account to the agent.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <Button 
                    onClick={handlePaid} 
                    disabled={processing || verificationCode.length < 1} 
                    className="w-full py-5 rounded-[2rem] font-black text-lg shadow-lg"
                  >
                    {processing ? 'Processing...' : 'Verify & Release Funds'}
                  </Button>
                  <button 
                    onClick={() => setFlowStep('payment')}
                    className="text-xs font-black text-muted-gray uppercase tracking-widest hover:text-charcoal transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* 1.5 Deriv Transfer Step (Withdrawal Only - Manual Path) */}
            {flowStep === 'deriv_transfer' && (
              <div className="p-4 sm:p-8 space-y-8 animate-in slide-in-from-right-8 duration-300">
                <div className="text-center">
                  <div className="bg-soft-green/10 p-6 rounded-full inline-block mb-4 text-soft-green">
                    <HugeiconsIcon icon={BankIcon} size={48} />
                  </div>
                  <h2 className="text-2xl font-black text-charcoal dark:text-white">Transfer Funds</h2>
                  <p className="text-muted-gray text-sm mt-1">Please transfer the exact amount to the agent on Deriv.</p>
                </div>

                <div className="space-y-6">
                  {/* Agent Details Card */}
                  <div className="bg-off-white dark:bg-gray-800 p-6 rounded-[2rem] border border-light-gray/20 dark:border-dark-border/20 space-y-6">
                    <div>
                      <p className="text-[10px] font-black uppercase text-muted-gray tracking-widest mb-1">Authorized Agent</p>
                      <p className="text-lg font-black text-charcoal dark:text-white">{order.merchant?.name || 'Unknown Agent'}</p>
                      {order.merchant?.deriv_name && (
                        <p className="text-[10px] font-bold text-soft-green uppercase tracking-tighter">Verified Deriv: {order.merchant.deriv_name}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase text-muted-gray tracking-widest mb-1">Agent CR Number</p>
                      <div className="flex items-center justify-between bg-white dark:bg-gray-700 p-4 rounded-xl border border-gray-100 dark:border-gray-600">
                        <p className="text-2xl font-black text-charcoal dark:text-white font-mono">{order.merchant?.deriv_id || 'Loading...'}</p>
                        <button onClick={() => {
                          if (order.merchant?.deriv_id) {
                            copyToClipboard(order.merchant.deriv_id);
                          } else {
                            addToast('Agent CR not available', 'error');
                          }
                        }} className="p-2 bg-gray-50 dark:bg-gray-600 hover:bg-gray-100 dark:hover:bg-gray-500 rounded-lg text-soft-green transition-colors">
                          <HugeiconsIcon icon={Copy01Icon} size={20} />
                        </button>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase text-muted-gray tracking-widest mb-1">Amount to Send</p>
                      <p className="text-4xl font-black text-charcoal dark:text-white">${parseFloat(payAmountUsd).toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Instructions */}
                  <div className="bg-blue-50 dark:bg-blue-900/10 p-6 rounded-[2rem] border border-blue-100 dark:border-blue-800/30">
                    <h3 className="text-sm font-black text-blue-800 dark:text-blue-200 mb-4 flex items-center gap-2">
                      <HugeiconsIcon icon={InformationCircleIcon} size={18} />
                      How to Transfer
                    </h3>
                    <ol className="list-decimal list-inside space-y-3 text-xs font-medium text-blue-700 dark:text-blue-300 leading-relaxed">
                      <li>Click <b>"Open Deriv"</b> below to go to the Payment Agent page.</li>
                      <li>Enter the Agent's <b>CR Number</b> and the <b>Exact Amount</b> (${parseFloat(payAmountUsd).toFixed(2)}).</li>
                      <li>Complete the transfer on Deriv.</li>
                      <li>Return here and click <b>"I Have Released USD"</b>.</li>
                    </ol>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <Button 
                    onClick={() => window.open('https://hub.deriv.com/tradershub/wallets/payment-agent', '_blank')} 
                    variant="secondary" 
                    className="w-full py-4 rounded-2xl font-black gap-2"
                  >
                    Open Deriv <HugeiconsIcon icon={ArrowRight01Icon} size={16} />
                  </Button>
                  <Button onClick={handlePaid} disabled={processing} className="w-full py-5 rounded-2xl font-black text-lg shadow-lg">
                    {processing ? 'Verifying...' : 'I Have Released USD'}
                  </Button>
                </div>
              </div>
            )}

            {/* 2. Waiting Room */}
            {flowStep === 'waiting' && (
              <div className="p-6 sm:p-12 text-center space-y-10">
                <div className="relative inline-block">
                  <FadeLoader color="#4ade80" />
                  <div className="absolute inset-0 flex items-center justify-center translate-y-16">
                    <span className="font-mono text-4xl font-black text-charcoal dark:text-white">{formatTime(timeLeft)}</span>
                  </div>
                </div>
                <div className="pt-8">
                  <div className="h-8 overflow-hidden relative">
                    <p key={activeTextIndex} className="text-xl font-black text-charcoal dark:text-white animate-in slide-in-from-bottom-4 duration-500">
                      {isWithdrawal ? withdrawalWaitingTexts[activeTextIndex % withdrawalWaitingTexts.length] : waitingTexts[activeTextIndex]}
                    </p>
                  </div>
                  <div className="flex items-center justify-center gap-2 mt-4">
                    <div className="relative">
                      <div className="w-2.5 h-2.5 bg-soft-green rounded-full shadow-[0_0_8px_rgba(74,222,128,0.5)]"></div>
                      <div className="absolute inset-0 w-2.5 h-2.5 bg-soft-green rounded-full animate-ping opacity-75"></div>
                    </div>
                    <p className="text-muted-gray text-[10px] uppercase font-black tracking-widest">Agent is Online</p>
                  </div>
                </div>
                <div className="bg-off-white/50 dark:bg-gray-800/50 p-6 rounded-[2.5rem] border border-light-gray/10 text-left">
                   <p className="text-xs text-muted-gray leading-relaxed">
                     {flowStep === 'waiting' && order?.transaction_type === 'withdrawal' && transaction?.status === 'verified'
                       ? "Success! Your USD has been received by the agent. Please stay on this page while the agent releases your Naira."
                       : "Your transfer has been logged. The agent will now verify and release your funds. Please stay on this page for instant confirmation."}
                   </p>
                </div>
                
                <div className="pt-4">
                  <button disabled className="flex items-center gap-2 mx-auto px-6 py-2 rounded-xl bg-gray-100 dark:bg-gray-800 text-[10px] font-black uppercase tracking-widest text-muted-gray opacity-50 cursor-not-allowed">
                    <HugeiconsIcon icon={AlertCircleIcon} size={14} />
                    Report Issue
                  </button>
                  <p className="mt-2 text-[9px] text-muted-gray/60 font-bold uppercase tracking-tighter italic">Reporting available if agent fails to confirm within 20 mins</p>
                </div>
              </div>
            )}

            {/* 3. Success */}
            {flowStep === 'success' && (
              <div className="p-12 text-center space-y-8">
                <div className="bg-soft-green/10 p-8 rounded-full inline-block">
                  <HugeiconsIcon icon={CheckmarkCircle01Icon} size={64} className="text-soft-green" />
                </div>
                <h2 className="text-4xl font-black text-charcoal dark:text-white tracking-tight">Order Complete!</h2>
                <div className="bg-soft-green/5 p-6 rounded-[2.5rem] border border-soft-green/10">
                   <p className="text-sm font-bold text-soft-green mb-1">{isWithdrawal ? 'Naira Released' : 'USD Received'}</p>
                   <p className="text-2xl font-black text-charcoal dark:text-white">{isWithdrawal ? `₦${payAmountNgn.toLocaleString()}` : `$${parseFloat(payAmountUsd).toFixed(2)}`}</p>
                </div>

                {!isWithdrawal && order.cashback_rate > 0 && (
                  <div className="bg-purple-500/10 p-6 rounded-[2.5rem] border border-purple-500/20 animate-bounce-slow">
                    <p className="text-[10px] font-black text-purple-500 uppercase tracking-[0.2em] mb-1">Cashback Earned!</p>
                    <p className="text-3xl font-black text-purple-600">₦{(parseFloat(payAmountUsd) * parseFloat(order.spread || 0) * parseFloat(order.cashback_rate)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    <button onClick={() => navigate('/commissions')} className="mt-3 text-[9px] font-black uppercase text-purple-500 hover:underline">View in Rewards Hub</button>
                  </div>
                )}

                <Button onClick={() => navigate('/')} className="w-full py-4 rounded-2xl font-black">Back to Home</Button>
              </div>
            )}

            {/* 4. Rejected */}
            {flowStep === 'rejected' && (
              <div className="p-12 text-center space-y-8 animate-in zoom-in duration-300">
                <div className="bg-red-50 dark:bg-red-900/20 p-8 rounded-full inline-block">
                  <HugeiconsIcon icon={AlertCircleIcon} size={64} className="text-red-500" />
                </div>
                <div>
                  <h2 className="text-3xl font-black text-charcoal dark:text-white tracking-tight">Order Rejected</h2>
                  <p className="text-muted-gray mt-2 font-medium">The agent declined this transaction.</p>
                </div>
                
                {rejectionReason && (
                  <div className="bg-off-white dark:bg-gray-800 p-4 rounded-2xl border border-light-gray/20 dark:border-dark-border/20">
                    <p className="text-[10px] font-black uppercase text-muted-gray mb-1">Reason</p>
                    <p className="text-sm font-bold text-charcoal dark:text-white">{rejectionReason}</p>
                  </div>
                )}

                <div className="bg-blue-50 dark:bg-blue-900/10 p-6 rounded-3xl text-left border border-blue-100 dark:border-blue-800/30">
                  <p className="text-xs text-blue-800 dark:text-blue-200 font-bold mb-4">Please contact the agent for a resolution or refund.</p>
                  <div className="space-y-2">
                    <p className="text-sm font-black text-charcoal dark:text-white">{order.merchant?.phone || 'No phone provided'}</p>
                    <p className="text-xs text-muted-gray font-medium select-all">{order.merchant?.email}</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={() => window.location.href = `mailto:support@safzi.com`} variant="secondary" className="flex-1 py-4 rounded-2xl font-black">Report Issue</Button>
                  <Button onClick={() => navigate('/')} className="flex-1 py-4 rounded-2xl font-black bg-gray-200 text-charcoal hover:bg-gray-300 dark:bg-gray-700 dark:text-white">Close</Button>
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      {/* Balance Sync Tutorial Modal */}
      <BalanceSyncTutorialModal
        isOpen={showBalanceSyncTutorial}
        onClose={() => setShowBalanceSyncTutorial(false)}
        onDismissed={() => {}}
      />
    </div>
  );
};

export default OrderPage;
